import { useEffect } from 'react';
import { Trans } from '@lingui/react/macro';

import { Comment } from '@/components/comments/Comment/Comment';
import { NewComment } from '@/components/comments/NewComment/NewComment';
import Drawer, { DrawerBody, DrawerFooter, DrawerHeader } from '@/design_system/Drawer';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useComments, useCreateComment } from '@/models/request';
import { hasPermission } from '@/models/user';
import { useClientToken, useCurrentOrganization, useCurrentSession } from '@/services/auth';
import { getWorkshopsWithClientCommunication } from '@/utils/permission';
import { useBrandOrAppRequestContext } from '@/utils/useBrandOrAppRequestContext';
import { useScrollIntoView } from '@/utils/useScrollIntoView';

import { IComment } from '../Comments/Comments';

import { useClientCommunicationDrawerContext } from './ClientCommunicationDrawerContext';
import { ClientCommunicationDrawerEmptyState } from './ClientCommunicationDrawerEmptyState';

export const ClientCommunicationDrawer = () => {
  const { request } = useBrandOrAppRequestContext();
  const { isClientCommunicationDrawerOpen, setIsClientCommunicationDrawerOpen } =
    useClientCommunicationDrawerContext();

  const { currentSession } = useCurrentSession();
  const [currentOrganization] = useCurrentOrganization();
  const clientToken = useClientToken();
  const { mutateAsync: createComment, isPending: isCreateCommentPending } = useCreateComment({
    requestId: request.id,
  });
  const { data: { comments } = { comments: [] } } = useComments({
    requestId: request.id,
    orderByCreatedAt: 'asc',
    visibility: ['client'],
    enabled: isClientCommunicationDrawerOpen,
  });

  const isReadonly =
    !request.organization.config.clientCommunication?.chat?.enabled ||
    (!clientToken &&
      !hasPermission(currentSession, 'write_client_communication', [
        {
          organizationId: currentOrganization?.id,
          entityId: null,
        },
        {
          entityId: request.afterSalesId,
        },
        {
          entityId: request.store?.id,
        },
        ...getWorkshopsWithClientCommunication(request.articles),
      ]));

  const contactName = clientToken
    ? request.organization.name
    : request.client?.name || (
        <Trans id="request.client-communication-drawer.title.the-client">the client</Trans>
      );

  return (
    <Drawer
      isOpen={isClientCommunicationDrawerOpen}
      onOpenChange={() => {
        setIsClientCommunicationDrawerOpen(false);
      }}
      style={{ maxWidth: '40.5rem' }}
    >
      <DrawerHeader>
        <Title size="300" weight="bold">
          <Trans id="request.client-communication-drawer.title">Contact {contactName}</Trans>
        </Title>
      </DrawerHeader>

      <DrawerBody style={{ display: 'flex' }}>
        {comments.length ? (
          <CommentsList comments={comments} />
        ) : (
          <ClientCommunicationDrawerEmptyState />
        )}
      </DrawerBody>

      {!isReadonly && (
        <DrawerFooter>
          <Stack
            style={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <NewComment
              variant="client-communication"
              onCommentCreated={async (data) => {
                await createComment(data);
              }}
              isLoading={isCreateCommentPending}
            />
          </Stack>
        </DrawerFooter>
      )}
    </Drawer>
  );
};

const CommentsList = ({ comments }: { comments: IComment[] }) => {
  const [lastCommentBottomRef, scrollToLastComment] = useScrollIntoView<HTMLDivElement>();

  // On initial render, scroll to the last comment immediately
  useEffect(() => {
    scrollToLastComment({ behavior: 'instant' });
  }, [scrollToLastComment]);

  // Every time a new comment is added, scroll to the last comment smoothly
  useEffect(() => {
    if (!comments.length) {
      return;
    }
    scrollToLastComment();
  }, [scrollToLastComment, comments.length]);

  return (
    <Stack padding="1.5rem" style={{ flex: 1, width: '100%' }}>
      {comments.map((comment) => {
        return (
          <div key={comment.id}>
            <Comment
              comment={comment}
              isReadOnly
              onCommentDeleted={async () => {}}
              onCommentUpdated={async () => {}}
            />
          </div>
        );
      })}
      <div ref={lastCommentBottomRef}></div>
    </Stack>
  );
};
